import { createContext, ReactNode, useContext, useState } from "react";
import { Toast, ToastContainer } from "react-bootstrap";
import styled from "styled-components";
import Toolbar, { FixedToolbarItem, FlexToolbarItem } from "./ToolBar";
import IconButton from "./IconButton";

type ToastManagerContextType = {
  publishToast: (message: ReactNode) => void;
  dismissToast: (toastID: string) => void;
  toastMap: ToastMap;
};

type ToastMap = Record<string, { message: ReactNode }>;

const StyledToastContainer = styled(ToastContainer)`
  top: 20px !important;
  .toast {
    --bs-toast-bg: var(--aa-surface-bg);
    --bs-toast-font-size: 1rem;
    --bs-toast-color: var(--bs-body-color);
    --bs-toast-padding-x: 20px;
    --bs-toast-padding-y: 20px;
  }
`;

const StyledIconButton = styled(IconButton)`
  color: var(--aa-text-secondary) !important;
`;

export const ToastManagerContext =
  createContext<ToastManagerContextType | null>(null);

export const ToastManagerProvider = ({ children }: { children: ReactNode }) => {
  const [toastMap, setToastMap] = useState<ToastMap>({});

  const toastManagerContext: ToastManagerContextType = {
    publishToast: (message: ReactNode) => {
      const toastID = crypto.randomUUID();
      setToastMap((prevToastMap) => ({
        ...prevToastMap,
        [toastID]: { message },
      }));
    },
    dismissToast: (toastID: string) => {
      setToastMap((prevToastMap) => {
        const updatedToastMap = { ...prevToastMap };
        delete updatedToastMap[toastID];
        return updatedToastMap;
      });
    },
    toastMap,
  };

  return (
    <ToastManagerContext.Provider value={toastManagerContext}>
      {children}
    </ToastManagerContext.Provider>
  );
};

export const ToastBox = () => {
  const context = useContext(ToastManagerContext);

  if (!context) {
    throw new Error("ToastManagerContext not found while rendering ToastBox.");
  }

  const { toastMap, dismissToast } = context;

  return (
    <StyledToastContainer position="top-center">
      {Object.entries(toastMap).map(([toastID, { message }]) => {
        return (
          <Toast
            key={toastID}
            onClose={() => {
              dismissToast(toastID);
            }}
            autohide
            delay={3000}
          >
            <Toast.Body>
              <Toolbar>
                <FlexToolbarItem>{message}</FlexToolbarItem>
                <FixedToolbarItem>
                  <StyledIconButton
                    className="bi-x-lg"
                    onClick={() => dismissToast(toastID)}
                  />
                </FixedToolbarItem>
              </Toolbar>
            </Toast.Body>
          </Toast>
        );
      })}
    </StyledToastContainer>
  );
};

export const useToastManager = () => {
  const context = useContext(ToastManagerContext);

  if (!context) {
    throw new Error(
      "useToastManager hook can only be used within a functional component that provides a ToastManagerContext."
    );
  }

  return {
    publishToast: context?.publishToast,
  };
};
